import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Button,
  IconCheck,
  IconLoading,
  IconWand,
  Modal,
  SelectField,
  TextField,
} from 'gatorcomponents';
import * as Styles from './ModalLogoGeneration.styles';
import useLocale from '@/hooks/useLocale';
import { useResize } from '@/hooks/useResize';
import { useDispatch, useSelector } from 'react-redux';
import { logoBuilderActions } from '@/redux/modules';
import { logoBuilderAnalytics } from '@/analytics/logoBuilder';
import HGBrandLogo from './HGBrandLogo';

const initialFormValues = {
  brandName: '',
  brandSlogan: '',
  category: '',
  color: '',
  hasError: false,
};

const ModalLogoGeneration = ({ selectedPaidPlan, setModalToShow }) => {
  const { logoBuilder: logoBuilderLocale } = useLocale();
  const { modalLogoGeneration: modalLogoGenerationLocale } = logoBuilderLocale;
  const [formValues, setFormValues] = React.useState(initialFormValues);
  const dispatch = useDispatch();
  const { loading } = useSelector(state => state.logoBuilder);

  const { width } = useResize();
  const mobile = useMemo(() => width < 600, [width]);

  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  const categoryOptions = modalLogoGenerationLocale.categories.map(option => ({ label: option, value: option }));

  const setNewFormValue = (field, value) => {
    setFormValues({ ...formValues, [field]: value });
  };

  const validateAndGoNextStep = () => {
    if (!formValues.brandName || !formValues.category || !formValues.color) {
      setFormValues({ ...formValues, hasError: true });
      logoBuilderAnalytics.formError();
    }

    if (formValues.brandName && formValues.category && formValues.color) {
      const payload = {
        brand_name: formValues.brandName,
        slogan: formValues.brandSlogan || 'Sem slogan',
        business_category: formValues.category,
        brand_color: formValues.color,
        purchase_intent: selectedPaidPlan,
      };

      dispatch(logoBuilderActions.logoGeneration.request(payload));
      selectedPaidPlan ? logoBuilderAnalytics.brandInfoCreateLogo() : logoBuilderAnalytics.brandInfoCreateLogoFree();
    }
  };

  const handleCloseModal = (backButtonClick) => {
    setFormValues(initialFormValues);
    setModalToShow(undefined);
    if (!backButtonClick) {
      selectedPaidPlan ? logoBuilderAnalytics.brandInfoCloseModal() : logoBuilderAnalytics.brandInfoCloseModalFree();
    }

    if (backButtonClick) {
      selectedPaidPlan ? logoBuilderAnalytics.brandInfoBack() : logoBuilderAnalytics.brandInfoBackFree();
    }
  };

  const formErrorRevalidation = useCallback(() => {
    if (formValues.brandName && formValues.category && formValues.color && formValues.hasError) {
      setFormValues({ ...formValues, hasError: false });
    }
  }, [formValues]);

  useEffect(() => {
    formErrorRevalidation();
  }, [formErrorRevalidation]);

  const ModalTitle = () => (
    <Styles.ModalTitleContainer>
      <HGBrandLogo mobile={mobile} />
      <Styles.ModalTitle>
        {logoBuilderLocale.logoBuilder}
      </Styles.ModalTitle>
    </Styles.ModalTitleContainer>
  );

  return (
    <Modal maxHeight={viewportHeight} maxWidth={viewportWidth} onClose={handleCloseModal} title={<ModalTitle />} noContentPadding={mobile}>
      <Styles.ModalContainer>
        {loading && (
          <Styles.LoadingWrapper>
            <IconLoading size="xl" />
            <Styles.LoadingText>
              {modalLogoGenerationLocale.generatingLogo}
            </Styles.LoadingText>
          </Styles.LoadingWrapper>
        )}

        {!loading && (
          <>

            <Styles.ModalSubtitle>
              <IconWand secondaryColor="activeLight" color="lowDark" />
              {' '}
              {selectedPaidPlan ? modalLogoGenerationLocale.thankYouForInterest : modalLogoGenerationLocale.tellUsAboutYourBrand}
            </Styles.ModalSubtitle>

            {selectedPaidPlan && (
              <Styles.ModalDescription>
                {modalLogoGenerationLocale.itIsATest}
              </Styles.ModalDescription>
            )}

            {selectedPaidPlan && (
              <Styles.TellUsTitle>
                {modalLogoGenerationLocale.tellUsAboutYourBrand}
              </Styles.TellUsTitle>
            )}

            <Styles.Form>
              <TextField
                name="brandName"
                label={modalLogoGenerationLocale.brandName}
                onChange={event => setNewFormValue('brandName', event.target.value)}
                value={formValues.brandName}
                status={formValues.hasError ? 'error' : 'normal'}
                testId="brandName"
                maxLength={100}
              />

              <TextField
                name="brandSlogan"
                label={modalLogoGenerationLocale.brandSlogan}
                onChange={event => setNewFormValue('brandSlogan', event.target.value)}
                value={formValues.brandSlogan}
                helpText={modalLogoGenerationLocale.helpTextSlogan}
                testId="brandSlogan"
                maxLength={100}
              />

              <SelectField
                placeholder={modalLogoGenerationLocale.category}
                withScroll
                options={categoryOptions}
                onClickSelect={option => setNewFormValue('category', option.value)}
                value={formValues.category}
                error={formValues.hasError}
                testId="category"
              />
            </Styles.Form>

            <Styles.ChooseColor>
              {modalLogoGenerationLocale.selectColor}
            </Styles.ChooseColor>

            <Styles.ColorCards>
              {modalLogoGenerationLocale.colors.map(color => (
                <Styles.ColorCard key={color.name} background={color.background} onClick={() => setNewFormValue('color', color.name)}>
                  {formValues.color === color.name && (
                    <Styles.SelectedColorFeedback>
                      <Styles.IconCheckWrapper>
                        <IconCheck color="primaryBlue" size="sm" />
                      </Styles.IconCheckWrapper>
                    </Styles.SelectedColorFeedback>
                  )}
                  <Styles.ColorName>
                    {color.name}
                  </Styles.ColorName>
                  <Styles.ColorDescription>
                    {color.description}
                  </Styles.ColorDescription>
                </Styles.ColorCard>
              ))}
            </Styles.ColorCards>

            {formValues.hasError && (
              <Styles.ErrorMessage>
                {modalLogoGenerationLocale.errorMessage}
              </Styles.ErrorMessage>
            )}

            <Styles.Actions>
              <Button label={modalLogoGenerationLocale.back} onClick={() => handleCloseModal(true)} variant="tertiary" size="large" />
              <Button label={modalLogoGenerationLocale.create} onClick={validateAndGoNextStep} size="large" disabled={formValues.hasError} />
            </Styles.Actions>
          </>
        )}
      </Styles.ModalContainer>
    </Modal>
  );
};


export default ModalLogoGeneration;
